
import type { PetSummoned, Polymorphed } from "../net/messages";
import { elem, styleInstaller } from "./chrome";

export interface PetActions {
  /** Send `PET_DISMISS`. There is deliberately no `onSummon` beside it. */
  onDismiss?: () => void;
}

export function remaining(seconds: number, elapsedMs: number): number {
  return Math.max(0, seconds - Math.floor(Math.max(0, elapsedMs) / 1000));
}

/** `m:ss`, because a marble runs for five minutes and a skilled one for
 *  thirty-five (`char_item.cpp:6944-6945`) -- long enough that a bare count of
 *  seconds stops being a number anybody reads. */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

const STYLE_ID = "pet-style";

const STYLE = `
.pet {
  position: fixed;
  right: 12px;
  top: 312px;
  z-index: 58;
  width: 196px;
  padding: 9px 11px 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(150, 190, 150, 0.24);
  pointer-events: auto;
  font: 12px/1.45 system-ui, sans-serif;
  color: #d8dee6;
}
.pet[hidden] { display: none; }
.pet-title {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9dc39a;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pet-close {
  margin-left: auto;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #ead7ae;
  font: 14px/1 sans-serif;
  cursor: pointer;
}
.pet-shape { color: #e0d0a8; margin: 2px 0; }
.pet-shape-left { color: #9fb6d4; font-size: 11px; }
.pet-warning { color: #9fa8b4; font-size: 11px; margin-top: 2px; }
.pet-name { color: #cfe3cd; margin: 2px 0; }
.pet-none { color: #8fa0b4; margin: 2px 0; }
.pet-button {
  width: 100%;
  margin-top: 7px;
  padding: 4px 0;
  border-radius: 6px;
  border: 1px solid rgba(150, 190, 150, 0.34);
  background: rgba(150, 190, 150, 0.1);
  color: #dcecda;
  font: inherit;
  cursor: pointer;
}
.pet-button:hover:not(:disabled) { background: rgba(150, 190, 150, 0.22); }
.pet-button:disabled { opacity: 0.35; cursor: default; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class PetPanel {
  private readonly root: HTMLElement;
  private readonly shape: HTMLElement;
  private readonly left: HTMLElement;
  private readonly warning: HTMLElement;
  private readonly name: HTMLElement;
  private readonly none: HTMLElement;
  private readonly dismiss: HTMLButtonElement;

  /** The transformation exactly as last sent, and the moment it arrived. The
   *  pair rather than a deadline, because the deadline is what would be wrong
   *  if the two clocks disagreed and this way the disagreement is visible. */
  private shapeFrame: Polymorphed | null = null;
  private shapeAtMs = 0;

  /** The pet exactly as last sent. Read by the button, so what it does is
   *  decided by what was *drawn*: a second copy of the state is the one that
   *  goes wrong. */
  private petFrame: PetSummoned | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: PetActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "pet");
    this.root.hidden = true;

    const title = elem(doc, "div", "pet-title", "Company");
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "pet-close";
    close.textContent = "×";
    close.title = "Close";
    close.setAttribute("aria-label", "Close company");
    close.addEventListener("click", () => this.setVisible(false));
    title.append(close);

    this.shape = elem(doc, "div", "pet-shape");
    this.left = elem(doc, "div", "pet-shape-left");
    this.warning = elem(doc, "div", "pet-warning");
    this.name = elem(doc, "div", "pet-name");
    this.none = elem(doc, "div", "pet-none", "Nothing follows you.");

    this.dismiss = doc.createElement("button");
    this.dismiss.type = "button";
    this.dismiss.className = "pet-button";
    this.dismiss.textContent = "Send it away";
    this.dismiss.addEventListener("click", () => {
      if (this.petFrame === null || this.petFrame.entity_id === 0) return;
      this.actions.onDismiss?.();
    });

    this.root.append(
      title, this.shape, this.left, this.warning, this.name, this.none,
      this.dismiss,
    );
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
  }

  /** Whether the panel is drawing a transformed character. For the frame loop,
   *  which has no other way to ask, and for the tests. */
  get transformed(): boolean {
    return this.shapeFrame !== null && this.shapeFrame.model !== "";
  }

  /** Whether the panel is drawing a pet. */
  get hasPet(): boolean {
    return this.petFrame !== null && this.petFrame.entity_id !== 0;
  }

  setShape(shape: Polymorphed, nowMs: number): void {
    this.shapeFrame = shape;
    this.shapeAtMs = nowMs;
    this.draw(nowMs);
  }

  /** The pet, or the absence of one. */
  setPet(pet: PetSummoned): void {
    this.petFrame = pet;
    this.draw(this.shapeAtMs);
  }

  tick(nowMs: number): void {
    if (!this.transformed) return;
    this.drawShape(nowMs);
  }

  private draw(nowMs: number): void {
    this.drawShape(nowMs);
    const has = this.hasPet;
    this.name.hidden = !has;
    const silent = has || this.transformed;
    this.none.hidden = silent;
    this.none.textContent = silent ? "" : "Nothing follows you.";
    this.dismiss.hidden = !has;
    this.dismiss.disabled = !has;
    // Emptied as well as hidden, both here and in `drawShape`. A hidden node
    // still carries its text -- to `textContent`, to a screen reader and to
    // anything that searches the page -- so a line left behind is a sentence
    // the panel is still saying while claiming not to.
    this.name.textContent =
      has && this.petFrame !== null ? `${this.petFrame.name} is with you.` : "";
  }

  private drawShape(nowMs: number): void {
    const shape = this.shapeFrame;
    const on = shape !== null && shape.model !== "";
    this.shape.hidden = !on;
    this.left.hidden = !on;
    this.warning.hidden = !on;
    if (!on || shape === null) {
      this.shape.textContent = "";
      this.left.textContent = "";
      this.warning.textContent = "";
      // The "nothing follows you" line comes back only when there is also no
      // pet -- otherwise it would sit under the pet's own name contradicting
      // it.
      this.none.hidden = this.hasPet;
      this.none.textContent = this.hasPet ? "" : "Nothing follows you.";
      return;
    }
    this.none.hidden = true;
    this.none.textContent = "";
    this.shape.textContent = `You are a ${shape.model}.`;
    this.left.textContent = `${clock(remaining(shape.seconds, nowMs - this.shapeAtMs))} left`;
    this.warning.textContent =
      "You cannot change your status, change your equipped items, or ride a Horse while you are transformed.";
  }
}
